import React, { memo, useCallback, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func } from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import IdeationActions from './Components/IdeationActions/IdeationActions'
import LoadingState from './Components/LoadingState/LoadingState'
import PasteUrlArea from './Components/PasteUrlArea/PasteUrlArea'
import PreviewWrap from './Components/PreviewWrap/PreviewWrap'
import UploadPrompt from './Components/UploadPrompt/UploadPrompt'
import useImageUploader from './Hooks/useImageUploader'

function ImageUploader({ sliceName, storeSchemaKey, setInputImage }) {
  const inputImageUrl = useThreeDStudioStore(state => state[sliceName].inputImageUrl)
  const { ideationFlowEnabled, extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  // The ideation entry points belong only to the Generate 3D Model panel, not the
  // shared retexture (textureSettings) uploader. `ideationFlowEnabled` is an actor-scoped
  // Flipper flag delivered client-side via pageConfig, so real visitors only ever see the
  // row when logged in with the flag on. Also render it during style extraction so its CSS
  // reaches the critical bundle and it never mounts unstyled when the flag resolves.
  const showIdeationActions = (ideationFlowEnabled || extractingCoreStyles) && sliceName === 'modelSettings'

  const {
    inputRef,
    allowedTypes,
    isLoading,
    onDragOver,
    onDrop,
    onFileChange,
    onFileClick,
    handleRemovePreview,
    handlePasteUrlUpload,
  } = useImageUploader(setInputImage, storeSchemaKey)

  const canAcceptFile = !isLoading
  const showUploadPrompt = !inputImageUrl && !isLoading

  const handleBrowseClick = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    onFileClick()
  }, [onFileClick])

  return (
    <>
      <div
        className={classnames(
          'image-uploader',
          inputImageUrl && 'image-uploader--has-preview',
          isLoading && 'image-uploader--loading',
        )}
        onDragOver={onDragOver}
        onDrop={canAcceptFile ? onDrop : undefined}
        onClick={showUploadPrompt ? onFileClick : undefined}
        role="region"
        aria-label="Image upload"
        aria-busy={isLoading}
      >
        <input
          ref={inputRef}
          type="file"
          accept={allowedTypes.join(',')}
          onChange={canAcceptFile ? onFileChange : undefined}
          className="image-uploader__input"
          aria-hidden
          tabIndex={-1}
          disabled={!canAcceptFile}
        />
        <div className="image-uploader__top">
          {isLoading && <LoadingState />}
          {inputImageUrl && !isLoading && (
            <PreviewWrap
              imageUrl={inputImageUrl}
              onRemovePreview={handleRemovePreview}
            />
          )}
          {showUploadPrompt && <UploadPrompt onBrowseClick={handleBrowseClick} />}
        </div>
      </div>
      <PasteUrlArea
        isLoading={isLoading}
        onPasteUrlUpload={handlePasteUrlUpload}
      />
      {showIdeationActions && <IdeationActions />}
    </>
  )
}

ImageUploader.propTypes = {
  sliceName: string.isRequired,
  storeSchemaKey: string.isRequired,
  setInputImage: func.isRequired,
}

export default memo(ImageUploader)
